import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, useTheme, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, Tooltip, alpha, TablePagination, Button, Snackbar
} from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import DefectViewerModal from '../MapView/modals/DefectViewerModal';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Cell, PieChart, Pie, Legend
} from 'recharts';
import { useAuth } from '../../hooks/auth/useAuth';
import { analyticsApi } from '../../services/api';
import DisciplineAccessControl from '../common/DisciplineAccessControl';

const DEFECT_COLORS = {
  percentage: ['#7c4dff', '#9c27b0', '#ce93d8', '#e1bee7'],
  binary: ['#1976d2', '#42a5f5', '#64b5f6', '#90caf9', '#bbdefb'],
  degree: ['#ff9800']
};

const getDefectColor = (code, index) => {
  if (['hm_corrosion', 'hm_paint_peeling', 'hm_vegetation', 'hm_moss'].includes(code)) {
    return DEFECT_COLORS.percentage[index % 4];
  }
  if (code === 'hm_slanting') return DEFECT_COLORS.degree[0];
  return DEFECT_COLORS.binary[index % 5];
};

const HighMastAnalytics = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { getUserOrganization } = useAuth();
  const organizationId = getUserOrganization()?.id;

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uninspectedData, setUninspectedData] = useState(null);
  const [uninspectedPage, setUninspectedPage] = useState(0);
  const [uninspectedRowsPerPage, setUninspectedRowsPerPage] = useState(5);

  // Defect chart drill-down (combining filters: defect type + severity)
  const [defectFilter, setDefectFilter] = useState({ typeCode: null, typeName: null, severityKey: null, severityName: null });
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState(null);
  const [drillPage, setDrillPage] = useState(0);
  const [drillRowsPerPage, setDrillRowsPerPage] = useState(5);
  const [selectedDefect, setSelectedDefect] = useState(null);
  const [defectViewerOpen, setDefectViewerOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  const fetchData = useCallback(async () => {
    if (!organizationId) return;
    try {
      setLoading(true);
      const [statsRes, uninspectedRes] = await Promise.all([
        analyticsApi.getHighMastDashboardStats(),
        analyticsApi.getHighMastDetails('uninspected', { limit: 1000, offset: 0 })
      ]);
      setStats(statsRes.data.data);
      setUninspectedData(uninspectedRes.data.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const drillActive = !!(defectFilter.typeCode || defectFilter.severityKey);

  const fetchDrill = useCallback(async () => {
    if (!defectFilter.typeCode && !defectFilter.severityKey) {
      setDrillData(null);
      return;
    }
    try {
      setDrillLoading(true);
      setDrillError(null);
      const params = {};
      if (defectFilter.typeCode) params.defect_type_code = defectFilter.typeCode;
      if (defectFilter.severityKey) params.severity_value = defectFilter.severityKey;
      const res = await analyticsApi.getHighMastDefectDetails(params);
      setDrillData(res.data.data);
      setDrillPage(0);
    } catch (err) {
      console.error('Error fetching defect drill-down:', err);
      setDrillError('Failed to load matching defects');
      setDrillData(null);
    } finally {
      setDrillLoading(false);
    }
  }, [defectFilter]);

  useEffect(() => { fetchDrill(); }, [fetchDrill]);

  // Toggle the defect-type filter from the bar chart (click same bar to clear)
  const handleTypeBarClick = (entry) => {
    const code = entry?.code || entry?.payload?.code;
    const name = entry?.name || entry?.payload?.name;
    if (!code) return;
    setDefectFilter(prev => prev.typeCode === code
      ? { ...prev, typeCode: null, typeName: null }
      : { ...prev, typeCode: code, typeName: name });
  };

  // Toggle the severity filter from a pie slice (click same slice to clear)
  const handleSeverityClick = (entry) => {
    const payload = entry?.payload || entry;
    const value = payload?.value;
    const name = payload?.name;
    if (name == null) return;
    const key = value == null ? 'unassigned' : String(value);
    setDefectFilter(prev => prev.severityKey === key
      ? { ...prev, severityKey: null, severityName: null }
      : { ...prev, severityKey: key, severityName: name });
  };

  const clearDefectFilter = () => setDefectFilter({ typeCode: null, typeName: null, severityKey: null, severityName: null });

  const handleViewDefect = (row) => {
    setSelectedDefect({ id: row.defect_id, defect_type_name: row.defect_type_name, detection_time: row.detection_time });
    setDefectViewerOpen(true);
  };

  const handleDefectViewerClose = () => {
    setDefectViewerOpen(false);
    setSelectedDefect(null);
  };

  const handleDrillChangePage = (event, newPage) => setDrillPage(newPage);
  const handleDrillChangeRowsPerPage = (event) => {
    setDrillRowsPerPage(parseInt(event.target.value, 10));
    setDrillPage(0);
  };

  const handleUninspectedChangePage = (event, newPage) => {
    setUninspectedPage(newPage);
  };

  const handleUninspectedChangeRowsPerPage = (event) => {
    setUninspectedRowsPerPage(parseInt(event.target.value, 10));
    setUninspectedPage(0);
  };

  const handleViewOnMap = (asset) => {
    if (asset.center_lat && asset.center_lng) {
      navigate('/map-view', {
        state: {
          selectedAsset: { id: asset.id, name: asset.name, code: asset.code, center_lat: asset.center_lat, center_lng: asset.center_lng },
          flyTo: { lat: asset.center_lat, lng: asset.center_lng, zoom: 18 },
          source: 'assetsPage' // force Asset Mode (tab 0) so the High Mast marker renders
        }
      });
    }
  };

  if (loading) {
    return (
      <DisciplineAccessControl requiredDiscipline="Vertical Asset">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#7c4dff' }} />
        </Box>
      </DisciplineAccessControl>
    );
  }

  if (error) {
    return (
      <DisciplineAccessControl requiredDiscipline="Vertical Asset">
        <Alert severity="error">{error}</Alert>
      </DisciplineAccessControl>
    );
  }

  if (!stats) return null;

  // Prepare chart data
  const defectChartData = stats.defectsByType.map((d, i) => ({
    name: d.defect_type_name,
    code: d.defect_type_code,
    count: d.count,
    fill: getDefectColor(d.defect_type_code, i)
  }));

  // Split severity data into percentage-based (Level 1/2/3) and binary (Yes/No)
  const percentageSeverityData = stats.defectsBySeverity
    .filter(d => d.severity_value >= 1 && d.severity_value <= 3)
    .map(d => ({ name: d.severity_name, value: d.severity_value, count: d.count, fill: d.color_code || '#9e9e9e' }));

  const binarySeverityData = stats.defectsBySeverity
    .filter(d => d.severity_value >= 4 && d.severity_value <= 5)
    .map(d => ({ name: d.severity_name, value: d.severity_value, count: d.count, fill: d.color_code || '#9e9e9e' }));

  const unassignedSeverityData = stats.defectsBySeverity
    .filter(d => d.severity_value == null)
    .map(d => ({ name: d.severity_name, value: d.severity_value, count: d.count, fill: d.color_code || '#9e9e9e' }));

  const hasPercentageData = percentageSeverityData.some(d => d.count > 0);
  const hasBinaryData = binarySeverityData.some(d => d.count > 0);
  const hasUnassigned = unassignedSeverityData.some(d => d.count > 0);
  const hasSeverityData = hasPercentageData || hasBinaryData || hasUnassigned;

  return (
    <DisciplineAccessControl requiredDiscipline="Vertical Asset">
      <Box sx={{ maxWidth: '100%' }}>

        {/* Charts Row */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>

          {/* Defect Distribution by Type */}
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: '1rem' }}>
              Defect Distribution by Type
              <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled', fontWeight: 400 }}>
                (click a bar to filter)
              </Typography>
            </Typography>
            {defectChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={defectChartData} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.15)} />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 12 }} />
                  <RechartsTooltip />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={20} onClick={handleTypeBarClick} style={{ cursor: 'pointer' }}>
                    {defectChartData.map((entry, index) => (
                      <Cell key={index} fill={entry.fill} opacity={defectFilter.typeCode && defectFilter.typeCode !== entry.code ? 0.35 : 1} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, border: '2px dashed', borderColor: 'divider', borderRadius: 2 }}>
                <Typography color="text.secondary">No defects recorded yet</Typography>
              </Box>
            )}
          </Paper>

          {/* Severity Breakdown — split by category */}
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: '1rem' }}>
              Defect Severity Breakdown
              <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled', fontWeight: 400 }}>
                (click a slice to filter)
              </Typography>
            </Typography>
            {hasSeverityData ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Two charts side by side */}
                <Box sx={{ display: 'grid', gridTemplateColumns: hasPercentageData && hasBinaryData ? '1fr 1fr' : '1fr', gap: 2 }}>
                  {/* Percentage-based severity (Level 1/2/3) */}
                  {hasPercentageData && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ textAlign: 'center', mb: 1, fontSize: '0.75rem' }}>
                        Percentage Severity
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mb: 1, color: 'text.disabled', fontSize: '0.65rem' }}>
                        Corrosion, Paint, Vegetation, Moss
                      </Typography>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie data={percentageSeverityData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={35} paddingAngle={3} label={({ name, count }) => `${count}`} onClick={handleSeverityClick} style={{ cursor: 'pointer' }}>
                            {percentageSeverityData.map((entry, index) => (
                              <Cell key={index} fill={entry.fill} opacity={defectFilter.severityKey && defectFilter.severityKey !== String(entry.value) ? 0.35 : 1} />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                          <Legend wrapperStyle={{ fontSize: '0.7rem' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  )}

                  {/* Binary severity (Yes/No) */}
                  {hasBinaryData && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ textAlign: 'center', mb: 1, fontSize: '0.75rem' }}>
                        Binary Detection
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mb: 1, color: 'text.disabled', fontSize: '0.65rem' }}>
                        Crack, Hinges, Cable, Foreign Objects
                      </Typography>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie data={binarySeverityData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={35} paddingAngle={3} label={({ name, count }) => `${count}`} onClick={handleSeverityClick} style={{ cursor: 'pointer' }}>
                            {binarySeverityData.map((entry, index) => (
                              <Cell key={index} fill={entry.fill} opacity={defectFilter.severityKey && defectFilter.severityKey !== String(entry.value) ? 0.35 : 1} />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                          <Legend wrapperStyle={{ fontSize: '0.7rem' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                </Box>

                {/* Unassigned severity (if any) */}
                {hasUnassigned && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                    {unassignedSeverityData.map((d, i) => (
                      <Chip key={i} label={`${d.name}: ${d.count}`} size="small" clickable onClick={() => handleSeverityClick(d)}
                        sx={{ bgcolor: alpha('#9e9e9e', defectFilter.severityKey === 'unassigned' ? 0.35 : 0.15), fontWeight: 600, fontSize: '0.7rem' }} />
                    ))}
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, border: '2px dashed', borderColor: 'divider', borderRadius: 2 }}>
                <Typography color="text.secondary">No severity data available</Typography>
              </Box>
            )}
          </Paper>
        </Box>

        {/* Defect Drill-down Panel (revealed when a chart segment is clicked) */}
        {drillActive && (
          <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: `1px solid ${alpha('#7c4dff', 0.25)}`, bgcolor: alpha('#7c4dff', 0.02) }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <FilterAltIcon fontSize="small" sx={{ color: '#7c4dff' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', mr: 1 }}>
                Matching Defects
              </Typography>
              {defectFilter.typeName && (
                <Chip label={`Type: ${defectFilter.typeName}`} size="small"
                  onDelete={() => setDefectFilter(prev => ({ ...prev, typeCode: null, typeName: null }))}
                  sx={{ bgcolor: alpha('#7c4dff', 0.1), color: '#7c4dff', fontWeight: 600 }} />
              )}
              {defectFilter.severityName && (
                <Chip label={`Severity: ${defectFilter.severityName}`} size="small"
                  onDelete={() => setDefectFilter(prev => ({ ...prev, severityKey: null, severityName: null }))}
                  sx={{ bgcolor: alpha('#7c4dff', 0.1), color: '#7c4dff', fontWeight: 600 }} />
              )}
              <Box sx={{ flexGrow: 1 }} />
              <Button size="small" onClick={clearDefectFilter} sx={{ color: 'text.secondary', textTransform: 'none' }}>
                Clear
              </Button>
            </Box>

            {drillLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} sx={{ color: '#7c4dff' }} />
              </Box>
            ) : drillError ? (
              <Alert severity="error">{drillError}</Alert>
            ) : drillData && drillData.length > 0 ? (
              <>
                <TableContainer sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: alpha('#7c4dff', 0.04) }}>
                        <TableCell sx={{ fontWeight: 600 }}>Asset Code</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Section</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Defect Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Severity</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Detected</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {drillData
                        .slice(drillPage * drillRowsPerPage, drillPage * drillRowsPerPage + drillRowsPerPage)
                        .map((row) => (
                        <TableRow key={row.defect_id} hover sx={{ '& .MuiTableCell-root': { py: 1.2 } }}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.asset_code}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{row.section || 'N/A'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{row.defect_type_name || '—'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={row.severity_name} size="small" sx={{ fontSize: '0.7rem', fontWeight: 600, bgcolor: alpha(row.color_code || '#9e9e9e', 0.15), color: row.color_code || '#616161' }} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{row.detection_time ? new Date(row.detection_time).toLocaleDateString() : '—'}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="View Defect">
                              <IconButton size="small" onClick={() => handleViewDefect(row)}
                                sx={{ color: '#7c4dff', bgcolor: alpha('#7c4dff', 0.08), mr: 0.5, '&:hover': { bgcolor: alpha('#7c4dff', 0.15) } }}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View on Map">
                              <IconButton size="small" onClick={() => handleViewOnMap({ id: row.asset_id, name: row.asset_name, code: row.asset_code, center_lat: row.center_lat, center_lng: row.center_lng })}
                                sx={{ color: '#7c4dff', bgcolor: alpha('#7c4dff', 0.08), '&:hover': { bgcolor: alpha('#7c4dff', 0.15) } }}>
                                <MapIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {drillData.length > drillRowsPerPage && (
                  <TablePagination
                    component="div"
                    count={drillData.length}
                    page={drillPage}
                    onPageChange={handleDrillChangePage}
                    rowsPerPage={drillRowsPerPage}
                    onRowsPerPageChange={handleDrillChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                  />
                )}
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 5, border: '2px dashed', borderColor: 'divider', borderRadius: 2 }}>
                <Typography color="text.secondary">No defects match this selection</Typography>
              </Box>
            )}
          </Paper>
        )}

        {/* Tables Row */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>

          {/* Top Assets by Defect Count */}
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: '1rem' }}>
              Top Assets by Defect Count
            </Typography>
            {stats.topAssetsByDefects.length > 0 ? (
              <TableContainer sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: alpha('#7c4dff', 0.04) }}>
                      <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Asset Code</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Section</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Defects</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.topAssetsByDefects.map((asset, index) => (
                      <TableRow key={asset.asset_id} hover sx={{ '& .MuiTableCell-root': { py: 1.2 } }}>
                        <TableCell>
                          <Chip label={index + 1} size="small" sx={{ fontWeight: 700, minWidth: 28, bgcolor: alpha('#7c4dff', 0.1), color: '#7c4dff' }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{asset.asset_code}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{asset.section_name || 'N/A'}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={asset.defect_count} size="small" sx={{ fontWeight: 600, bgcolor: alpha('#f44336', 0.1), color: '#f44336' }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6, border: '2px dashed', borderColor: 'divider', borderRadius: 2 }}>
                <Typography color="text.secondary">No defects recorded yet</Typography>
              </Box>
            )}
          </Paper>

          {/* Uninspected Assets */}
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: '1rem' }}>
              Uninspected Assets
            </Typography>
            {uninspectedData && uninspectedData.length > 0 ? (
              <TableContainer sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: alpha(theme.palette.warning.main, 0.04) }}>
                      <TableCell sx={{ fontWeight: 600 }}>Asset Code</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Section</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Height</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uninspectedData
                      .slice(uninspectedPage * uninspectedRowsPerPage, uninspectedPage * uninspectedRowsPerPage + uninspectedRowsPerPage)
                      .map((asset) => (
                      <TableRow key={asset.id} hover sx={{ '& .MuiTableCell-root': { py: 1.2 } }}>
                        <TableCell>
                          <Chip label={asset.code} size="small" sx={{ fontSize: '0.75rem', fontWeight: 600, bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.dark }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{asset.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{asset.section || 'N/A'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{asset.height ? `${asset.height}m` : '—'}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="View on Map">
                            <IconButton size="small" onClick={() => handleViewOnMap(asset)}
                              sx={{ color: '#7c4dff', bgcolor: alpha('#7c4dff', 0.08), '&:hover': { bgcolor: alpha('#7c4dff', 0.15) } }}>
                              <MapIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6, border: '2px dashed', borderColor: 'divider', borderRadius: 2 }}>
                <Typography color="text.secondary">All assets have been inspected</Typography>
              </Box>
            )}
            {uninspectedData && uninspectedData.length > uninspectedRowsPerPage && (
              <TablePagination
                component="div"
                count={uninspectedData.length}
                page={uninspectedPage}
                onPageChange={handleUninspectedChangePage}
                rowsPerPage={uninspectedRowsPerPage}
                onRowsPerPageChange={handleUninspectedChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            )}
          </Paper>
        </Box>
      </Box>

      <DefectViewerModal
        open={defectViewerOpen}
        onClose={handleDefectViewerClose}
        defect={selectedDefect}
        onError={(msg) => setSnackbar({ open: true, message: msg, severity: 'error' })}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DisciplineAccessControl>
  );
};

export default HighMastAnalytics;
